import {Component} from '@angular/core';
import {Auth, AuthResponse, Transport, TransportEvent, TransportResponse} from "../../app.module";
import { ActivatedRoute, Router } from '@angular/router';
import {environment} from "../../../environments/environment";
import axios from "axios";
import moment from "moment/moment";

@Component({
    selector: 'app-view',
    templateUrl: './view.component.html',
    styleUrls: ['./view.component.css'],
    standalone: false
})
export class ViewComponent {
  public transport: Transport | null = null;
  public transportEvents: TransportEvent[] = [];
  public isLoading: boolean = false;
  public step: number = 1;

  /**
   * Constructor for the Angular component responsible for handling route parameters.
   *
   * This constructor initializes the component and subscribes to route parameter changes.
   * When the "trackingCode" route parameter is present, it triggers the transport event handling
   * and progress bar update.
   *
   * @param {ActivatedRoute} activatedRoute - The ActivatedRoute service for accessing route parameters.
   * @param {Router} router - The Router service for navigating within the application.
   */
  constructor(private activatedRoute: ActivatedRoute, private router: Router) {
    this.activatedRoute.paramMap.subscribe(params => {
      const trackingCode = params.get("trackingCode");
      if (trackingCode) {
        this.transportEventHandler(trackingCode).then(() => {
          this.setProgressbar(this.transportEvents)
        });
      }
    })
  }

  /**
   * Set and update a progress bar based on the current transport events.
   *
   * This function is responsible for setting and updating a progress bar based on the
   * state of transport events. It finds the event currently in progress and increments
   * the progress step until it reaches the specified event's order. It uses a setInterval
   * to control the step increment at a regular interval.
   *
   * @param {TransportEvent[]} transportEvents - An array of transport events to track progress.
   */
  setProgressbar(transportEvents: TransportEvent[]) {
    let eventStep = 0
    const eventInProgress = transportEvents.find(event => event.isInProgress);
    if (eventInProgress) {
      eventStep = eventInProgress.order + 1;
    } else {
      const allEventsDone = transportEvents.every(event => event.isDone);

      if (allEventsDone) {
        eventStep = transportEvents.length;
      } else {
        eventStep = 1;
      }
    }

    const interval = setInterval(() => {
      if (this.step < eventStep) {
        this.step = this.step + 1;
      } else {
        clearInterval(interval);
      }
    }, 200);
  }

  /**
   * Navigate back to the search page.
   */
  goBack() {
    this.router.navigate(["/"]).then(() => {})
  }

  /**
   * Calculate the background position for a dynamic linear gradient based on the current step.
   *
   * This function calculates the background position percentage for a dynamic linear gradient
   * based on the current step, considering the total number of transport events. The result
   * is used to position the gradient stop along the horizontal axis.
   *
   * @returns number A CSS-compatible background position string in percentage.
   */
  positionStop() {
    const count: number = this.transportEvents.length;
    const step: number = this.step - 1; // from zero
    const result: number = 100 * ((count - step - 1) / (count - 1));

    return `${result}%`
  }

  /**
   * Generate CSS styles for creating a dynamic linear gradient background.
   *
   * This function generates a set of CSS styles that create a linear gradient
   * background using specified colors and positions. The linear gradient moves
   * from one color to another using predefined CSS variables for color stops.
   *
   * @returns An object containing CSS style properties.
   */
  styles = () => ({
    'background': `linear-gradient(
          to right,
          hsl(var(--su)) 50%,
          hsl(var(--in)) 50%,
          hsl(var(--nc)) 50%
        )`
    ,
    'background-position': this.positionStop(),
    'background-size': '200%',
  })

  /**
   *
   * @param {String} clientId
   * @param {String} clientSecret
   */
  getAuth(clientId: string, clientSecret: string): Promise<Auth> {
    const options = {
      headers: { 'content-type': 'application/json' },
      client_id: clientId,
      client_secret: clientSecret,
      audience: environment.AUTH0_AUDIENCE,
      grant_type: 'client_credentials'
    }
    this.isLoading = true;
    return new Promise((resolve, reject): void => {
      axios.post(environment.AUTH0_LINK, options).then((res: AuthResponse) => {
        const accessToken = res.data.access_token;
        const tokenType = res.data.token_type;
        resolve({ tokenType, accessToken })
      }).catch(err => {
        console.error("Something went wrong went with authorization: ", err)
        this.isLoading = false;
        reject(err)
      })
    })
  }

  /**
   *
   * @param {String} tokenType
   * @param {String} accessToken
   * @param url
   */
  getData(tokenType: string, accessToken: string, url: string): Promise<TransportResponse> {
    const options = {
      headers: {
        'x-api-key': environment.EXPLECT_API_KEY,
        Authorization: `${tokenType} ${accessToken}`,
        "content-type": "application/json",
      },
    }

    return new Promise((resolve, reject) => {
      axios.get(url, options).then(res => {
        this.isLoading = false;
        resolve(res.data)
      }).catch(err => {
        this.isLoading = false;
        console.error("Something went wrong went while getting data: ", err)
        reject(err)
      })
    })
  }

  /**
   * Format a date to a string
   * @param value
   */
  formatDate(value: Date): string | null {
    if (value) {
      return moment(String(value)).format('YYYY/MM/DD')
    } else {
      return null;
    }
  }

  /**
   * Format a time string to HH:mm
   * @param value
   */
  formatTime(value: string): string | null {
    if (value) {
      return value.substring(0, 5)
    } else {
      return null;
    }
  }

  /**
   * Handle transport event data based on a tracking code.
   *
   * This asynchronous function is responsible for handling transport event data
   * based on a provided tracking code. It retrieves authentication information,
   * makes an API request to fetch transport data, and processes the response.
   *
   * @param {string} trackingCode - The tracking code associated with the transport event.
   * @returns {Promise<void>} A promise that resolves when data processing is complete.
   */
  async transportEventHandler(trackingCode: string): Promise<void> {
    const auth: Auth = await this.getAuth(environment.CLIENTID, environment.CLIENTSECRET);
    const url: string = environment.EXPLECT_API_URL + `transport/tracking/${trackingCode}`;
    const response: TransportResponse = await this.getData(auth.tokenType, auth.accessToken, url)
    if (response.statusCode === 0) {
      this.transport = response.transport;
      this.transportEvents = response.transport.events.map((event: any) => {
        return {
          ...event,
          startDate: this.formatDate(event.startDate),
          endDate: this.formatDate(event.endDate),
          startTime: this.formatTime(event.startTime),
          endTime: this.formatTime(event.endTime),
        }
      });
    }
  }

  /**
   * Determines the appropriate CSS class for a given transport event based on its status.
   *
   * @param {TransportEvent} event - The transport event object to evaluate.
   * @return {"bg-neutral-content" | "bg-info" | "bg-success"} The CSS class name representing the event's status.
   */
  getEventClass(event: TransportEvent): "bg-neutral-content" | "bg-info" | "bg-success" {
    if (!event) {
      return "bg-neutral-content"
    }

    if (event.isDone) {
      return "bg-success"
    } else if (event.isInProgress) {
      return "bg-info"
    } else {
      return "bg-neutral-content"
    }
  }
}
